'use client'

import type { RouteModel } from '@polaris/core/models'
import { featureFlagsAtom } from '@polaris/core/stores'
import { formatCoin, formatPrice } from '@polaris/core/utils'
import { useAtom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import { ChevronDown, ChevronUp, CircleAlert } from 'lucide-react'
import { useRouter } from 'next/navigation'
import { useCallback, useState } from 'react'
import { useMostRecentFailedTrade } from '@/src/hooks/use-most-recent-failed-trade'
import {
  ListRowButton,
  ListRowSide,
  ListRowSideDescription,
  ListRowSideName,
} from '../ui/list-row'
import TokenImg from '../ui/token-img'

// Atom for managing dismissed trade IDs with localStorage persistence
const dismissedTradesAtom = atomWithStorage<string[]>(
  'polaris-dismissed-failed-trades',
  [],
)

/**
 * Component that displays up to 3 recent failed trades as stacked notifications
 * below the portfolio balance. Shows collapsed state with expand/collapse functionality.
 */
export function FailedTradeNotification() {
  const [featureFlags] = useAtom(featureFlagsAtom)
  const recentFailedTrades = useMostRecentFailedTrade()
  const router = useRouter()
  const [dismissedTradeIds, setDismissedTradeIds] = useAtom(dismissedTradesAtom)
  const [isExpanded, setIsExpanded] = useState(false)

  // Convert array to Set for easier lookup
  const dismissedTrades = new Set(dismissedTradeIds)

  const handleTradeClick = useCallback(
    (tradeId: string) => {
      // Dismiss the trade (mark as clicked)
      setDismissedTradeIds((prev) => [...new Set([...prev, tradeId])])

      // Navigate to the failed trade route, same as ActivityItem does
      router.push(`/route/${tradeId}`)
    },
    [router, setDismissedTradeIds],
  )

  // Filter out dismissed trades
  const visibleTrades = recentFailedTrades.filter(
    (trade) => !dismissedTrades.has(trade.ID),
  )

  const handleDismissAll = useCallback(() => {
    const newTradeIds = visibleTrades.map((t) => t.ID)
    setDismissedTradeIds((prev) => [...new Set([...prev, ...newTradeIds])])
  }, [visibleTrades, setDismissedTradeIds])

  // Return null if feature flag is disabled
  if (!featureFlags.abortNotifications) {
    return null
  }

  // Don't render if no visible failed trades
  if (!visibleTrades.length) {
    return null
  }

  const tradeCount = visibleTrades.length

  return (
    <div className='flex flex-col relative z-40'>
      {tradeCount === 1 ? (
        // Single trade - show individual notification
        <FailedTradeItem
          trade={visibleTrades[0]}
          onClick={() => handleTradeClick(visibleTrades[0].ID)}
        />
      ) : (
        // Multiple trades - show stacked notification like design
        <div className='flex flex-col'>
          {/* Collapsed state - stacked cards */}
          {!isExpanded && (
            <div className='relative h-[6.5em]'>
              {/* Background stacked rows for visual effect */}
              {tradeCount >= 3 && (
                <div className='absolute top-0 left-0 flex items-center p-4 gap-4 h-20 rounded-2xl z-[1] translate-y-6 translate-x-4 border border-border1 bg-surface1-100 pointer-events-none w-[20.5em]' />
              )}

              {tradeCount >= 2 && (
                <div className='absolute top-0 left-0 flex items-center p-4 gap-4 h-20 rounded-2xl z-[2] translate-y-3 translate-x-2 border border-border1 bg-surface1-100 pointer-events-none w-[21.5em]' />
              )}

              {/* Main functional row */}
              <button
                onClick={() => setIsExpanded(true)}
                className='flex items-center gap-4 p-4 rounded-2xl absolute top-0 left-0 z-[3] border border-border1 bg-surface1-100 w-[22.5em] h-20 transition-colors'
              >
                {/* Token logos stacked */}
                <div className='relative size-10 flex items-center justify-center flex-shrink-0'>
                  <MultiTradeLogos trades={visibleTrades} count={tradeCount} />
                </div>

                {/* Content section */}
                <ListRowSide containerClassName='flex-1'>
                  <ListRowSideName className='text-primary'>
                    {tradeCount} trades failed
                  </ListRowSideName>
                  <ListRowSideDescription className='text-orange-300'>
                    Action required
                  </ListRowSideDescription>
                </ListRowSide>

                {/* Dropdown arrow */}
                <div className='flex-shrink-0'>
                  <div className='flex items-center justify-center p-1 size-8 rounded-full bg-white/12'>
                    <div className='flex items-center justify-center size-6'>
                      <ChevronDown className='size-4 text-primary' />
                    </div>
                  </div>
                </div>
              </button>
            </div>
          )}

          {/* Expanded state */}
          {isExpanded && (
            <ListRowButton
              onClick={() => setIsExpanded(false)}
              size='lg'
              className='gap-4 rounded-2xl bg-surface1'
            >
              {/* Token logos stacked */}
              <div className='relative size-10 flex items-center justify-center flex-shrink-0'>
                <MultiTradeLogos trades={visibleTrades} count={tradeCount} />
              </div>

              {/* Content section */}
              <ListRowSide containerClassName='flex-1'>
                <ListRowSideName className='text-primary'>
                  {tradeCount} trades failed
                </ListRowSideName>
                <ListRowSideDescription className='text-orange-300'>
                  Action required
                </ListRowSideDescription>
              </ListRowSide>

              {/* Dropdown arrow */}
              <div className='flex-shrink-0'>
                <div className='flex items-center justify-center p-1 size-8 rounded-full bg-white/12'>
                  <div className='flex items-center justify-center size-6'>
                    <ChevronUp className='size-4 text-primary' />
                  </div>
                </div>
              </div>
            </ListRowButton>
          )}

          {/* Expanded content */}
          {isExpanded && (
            <div className='flex flex-col gap-2 mt-2'>
              {/* Header with actions */}
              <div className='flex justify-between items-center px-4 py-2'>
                <button
                  onClick={handleDismissAll}
                  className='text-sm text-secondary hover:text-primary transition-colors'
                >
                  Dismiss all
                </button>
                <button
                  onClick={() => setIsExpanded(false)}
                  className='text-sm text-secondary hover:text-primary transition-colors'
                >
                  Show less
                </button>
              </div>

              {/* Individual trades */}
              <div className='flex flex-col gap-2'>
                {visibleTrades.map((trade) => (
                  <FailedTradeItem
                    key={trade.ID}
                    trade={trade}
                    onClick={() => handleTradeClick(trade.ID)}
                    showAmount={true}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Individual failed trade notification item
 */
function FailedTradeItem({
  trade,
  onClick,
  showAmount = false,
}: {
  trade: RouteModel
  onClick: () => void
  showAmount?: boolean
}) {
  const usdValue = formatPrice(trade.AmountOut * trade.OutputAssetUSDPrice)

  const cryptoAmount = formatCoin({
    denom: trade.OutputAssetMetadata.ticker,
    amount: trade.AmountOut,
  })

  return (
    <ListRowButton
      onClick={onClick}
      size='lg'
      className='gap-4 rounded-2xl border border-border1 bg-surface1-100 hover:bg-surface2-100'
    >
      {/* Token image with alert overlay - size 40x40 container */}
      <div className='relative size-10 flex items-center justify-center flex-shrink-0'>
        <TokenImg
          src={trade.OutputAssetMetadata.logo_img_url}
          alt={trade.OutputAssetMetadata.ticker}
          className='rounded-full size-5'
        />
        {/* Alert icon overlay - centered in bottom-right quadrant */}
        <div className='absolute -bottom-1 -right-1 size-6 bg-background flex items-center justify-center rounded-full'>
          <CircleAlert className='size-5 text-danger-text' />
        </div>
      </div>

      {/* Content section - stacked layout */}
      <ListRowSide containerClassName='flex-1'>
        <ListRowSideName className='text-primary'>Trade failed</ListRowSideName>
        <ListRowSideDescription className='text-orange-300'>
          Action required
        </ListRowSideDescription>
      </ListRowSide>

      {/* Trailing content - show amounts only when expanded */}
      {showAmount && (
        <ListRowSide className='text-right flex-shrink-0'>
          <ListRowSideName className='text-primary font-normal'>
            {usdValue}
          </ListRowSideName>
          <ListRowSideDescription>{cryptoAmount}</ListRowSideDescription>
        </ListRowSide>
      )}
    </ListRowButton>
  )
}

/**
 * Component to display multiple trade token logos in a stacked formation
 */
function MultiTradeLogos({
  trades,
  count,
}: {
  trades: RouteModel[]
  count: number
}) {
  if (count === 2) {
    // Two trades - show in grid layout with centered alert icon
    return (
      <div className='relative'>
        <div className='grid grid-cols-2 gap-0.5 size-10'>
          <div className='flex items-center justify-center'>
            <TokenImg
              src={trades[0].OutputAssetMetadata.logo_img_url}
              alt={trades[0].OutputAssetMetadata.ticker}
              width={19}
              height={19}
              className='rounded-full flex-shrink-0'
            />
          </div>
          <div className='flex items-center justify-center'>
            <TokenImg
              src={trades[1].OutputAssetMetadata.logo_img_url}
              alt={trades[1].OutputAssetMetadata.ticker}
              width={19}
              height={19}
              className='rounded-full flex-shrink-0'
            />
          </div>
        </div>
        {/* Alert icon overlay - centered in bottom row */}
        <div className='absolute -bottom-1 left-1/2 transform -translate-x-1/2 size-6 bg-background flex items-center justify-center rounded-full'>
          <CircleAlert className='size-5 text-danger-text' />
        </div>
      </div>
    )
  }

  // Three or more trades - show grid
  return (
    <div className='relative'>
      <div className='grid grid-cols-2 gap-0.5 size-10'>
        {trades.slice(0, 3).map((trade, _index) => (
          <div key={trade.ID} className='flex items-center justify-center'>
            <TokenImg
              src={trade.OutputAssetMetadata.logo_img_url}
              alt={trade.OutputAssetMetadata.ticker}
              width={19}
              height={19}
              className='rounded-full flex-shrink-0'
            />
          </div>
        ))}
        {count > 3 && (
          <div className='flex items-center justify-center'>
            <div className='w-[19px] h-[19px] bg-surface1 rounded-full flex items-center justify-center flex-shrink-0'>
              <span className='text-xs text-secondary font-medium'>+</span>
            </div>
          </div>
        )}
      </div>
      {/* Alert icon overlay - centered in bottom-right quadrant */}
      <div className='absolute -bottom-1 -right-1 size-6 bg-background flex items-center justify-center rounded-full'>
        <CircleAlert className='size-5 text-danger-text' />
      </div>
    </div>
  )
}
